import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CalendarClock, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";
import { getApiErrorLines } from "../../utils/formErrors";
import { toastApiError, toastErrors } from "../../utils/toastErrors";
import { toPersianDigits } from "../../utils/numberFormat";

// Automatic NTSW full-sync schedules (jobs/schedules.py). Each schedule is a
// cron expression in Tehran time; the form builds it from simple choices.

const API = "/ntsw/schedules/";
const LABELS = { name: "نام", kind: "نوع", cron: "زمان‌بندی" };

const KINDS = [
  { value: "daily", label: "روزانه" },
  { value: "weekly", label: "هفتگی" },
  { value: "monthly", label: "ماهانه" },
  { value: "hourly", label: "هر چند ساعت" },
  { value: "custom", label: "پیشرفته (cron)" },
];

// Cron day-of-week numbers (0 = Sunday), in Iranian week order.
const WEEKDAYS = [
  { value: 6, label: "شنبه" },
  { value: 0, label: "یکشنبه" },
  { value: 1, label: "دوشنبه" },
  { value: 2, label: "سه‌شنبه" },
  { value: 3, label: "چهارشنبه" },
  { value: 4, label: "پنجشنبه" },
  { value: 5, label: "جمعه" },
];
const HOUR_STEPS = [2, 3, 4, 6, 8, 12];

const OUTCOME_TONES = {
  queued: "bg-emerald-50 text-emerald-700",
  skipped_running: "bg-amber-50 text-amber-700",
  skipped_no_credentials: "bg-rose-50 text-rose-700",
  skipped_read_only: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700",
};

const EMPTY_FORM = {
  name: "",
  kind: "daily",
  time: "02:00",
  weekdays: [6],
  dayOfMonth: 1,
  everyHours: 6,
  minute: 0,
  cron: "0 2 * * *",
  is_active: true,
};

const pad = (n) => String(n).padStart(2, "0");
const faTime = (h, m) => toPersianDigits(`${pad(h)}:${pad(m)}`);

const splitTime = (time) => {
  const [h, m] = String(time || "00:00").split(":").map((part) => Number(part) || 0);
  return [Math.min(Math.max(h, 0), 23), Math.min(Math.max(m, 0), 59)];
};

export function buildCron(form) {
  const [h, m] = splitTime(form.time);
  switch (form.kind) {
    case "daily":
      return `${m} ${h} * * *`;
    case "weekly":
      return `${m} ${h} * * ${[...form.weekdays].sort((a, b) => a - b).join(",")}`;
    case "monthly":
      return `${m} ${h} ${form.dayOfMonth} * *`;
    case "hourly":
      return `${form.minute} */${form.everyHours} * * *`;
    default:
      return form.cron.trim().replace(/\s+/g, " ");
  }
}

/** The form for an existing schedule; anything unrecognised opens as custom. */
export function formFromSchedule(schedule) {
  const form = { ...EMPTY_FORM, name: schedule.name, kind: schedule.kind, cron: schedule.cron, is_active: schedule.is_active };
  const [m, h, dom, , dow] = schedule.cron.split(" ");
  const time = `${pad(h)}:${pad(m)}`;
  if (schedule.kind === "daily") return { ...form, time };
  if (schedule.kind === "weekly") return { ...form, time, weekdays: dow.split(",").map(Number) };
  if (schedule.kind === "monthly") return { ...form, time, dayOfMonth: Number(dom) };
  if (schedule.kind === "hourly") return { ...form, minute: Number(m), everyHours: Number(h.replace("*/", "")) };
  return { ...form, kind: "custom" };
}

export function describe(schedule) {
  const [m, h, dom, , dow] = schedule.cron.split(" ");
  switch (schedule.kind) {
    case "daily":
      return `هر روز ساعت ${faTime(h, m)}`;
    case "weekly": {
      const days = WEEKDAYS.filter((d) => dow.split(",").map(Number).includes(d.value)).map((d) => d.label);
      return `هر هفته، ${days.join("، ")}، ساعت ${faTime(h, m)}`;
    }
    case "monthly":
      return `روز ${toPersianDigits(dom)} هر ماه، ساعت ${faTime(h, m)}`;
    case "hourly":
      return `هر ${toPersianDigits(h.replace("*/", ""))} ساعت، دقیقه ${toPersianDigits(m)}`;
    default:
      return `سفارشی: ${schedule.cron}`;
  }
}

const when = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fa-IR", {
      timeZone: "Asia/Tehran",
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

function formProblems(form) {
  const problems = [];
  if (!form.name.trim()) problems.push("نام: الزامی است.");
  if (form.kind === "weekly" && !form.weekdays.length) problems.push("روزهای هفته: دست‌کم یک روز را انتخاب کنید.");
  if (form.kind === "custom" && !form.cron.trim()) problems.push("زمان‌بندی: الزامی است.");
  return problems;
}

function ScheduleForm({ initial, saving, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState({ runs: [], error: "", loading: false });
  const cron = buildCron(form);
  const set = (field) => (event) => setForm((f) => ({ ...f, [field]: event.target.value }));

  useEffect(() => {
    if (!cron.trim()) return undefined;
    setPreview((p) => ({ ...p, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.post(`${API}preview/`, { cron });
        setPreview({ runs: data.next_runs || [], error: "", loading: false });
      } catch (error) {
        setPreview({ runs: [], error: getApiErrorLines(error, LABELS)[0], loading: false });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [cron]);

  const toggleDay = (value) =>
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(value) ? f.weekdays.filter((d) => d !== value) : [...f.weekdays, value],
    }));

  const submit = (event) => {
    event.preventDefault();
    const problems = formProblems(form);
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    onSave({ name: form.name.trim(), kind: form.kind, cron, is_active: form.is_active });
  };

  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500";

  return (
    <form onSubmit={submit} noValidate className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-slate-600">نام <span className="text-rose-500">*</span></span>
          <input value={form.name} onChange={set("name")} maxLength={100} placeholder="مثلاً «همگام‌سازی شبانه»" className={input} />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-slate-600">تکرار</span>
          <select value={form.kind} onChange={set("kind")} className={input}>
            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </label>
      </div>

      {["daily", "weekly", "monthly"].includes(form.kind) && (
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">ساعت اجرا (به وقت تهران)</span>
            <input type="time" value={form.time} onChange={set("time")} dir="ltr" className={`${input} text-right`} />
          </label>
          {form.kind === "monthly" && (
            <label className="space-y-1 text-sm">
              <span className="text-slate-600">روز ماه (میلادی)</span>
              <select value={form.dayOfMonth} onChange={set("dayOfMonth")} className={input}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{toPersianDigits(d)}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {form.kind === "weekly" && (
        <fieldset className="space-y-2 text-sm">
          <legend className="text-slate-600">روزهای هفته</legend>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                aria-pressed={form.weekdays.includes(day.value)}
                className={`rounded-full border px-3 py-1 ${form.weekdays.includes(day.value) ? "border-sky-600 bg-sky-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {form.kind === "hourly" && (
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">هر چند ساعت</span>
            <select value={form.everyHours} onChange={set("everyHours")} className={input}>
              {HOUR_STEPS.map((n) => <option key={n} value={n}>{`هر ${toPersianDigits(n)} ساعت`}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">در دقیقه</span>
            <select value={form.minute} onChange={set("minute")} className={input}>
              {[0, 15, 30, 45].map((n) => <option key={n} value={n}>{toPersianDigits(n)}</option>)}
            </select>
          </label>
        </div>
      )}

      {form.kind === "custom" && (
        <label className="block space-y-1 text-sm">
          <span className="text-slate-600">عبارت cron</span>
          <input value={form.cron} onChange={set("cron")} dir="ltr" placeholder="0 2 * * *" className={`${input} font-mono`} />
          <span className="block text-xs leading-6 text-slate-500">
            پنج بخش، به وقت تهران: دقیقه، ساعت، روز ماه، ماه، روز هفته (۰ = یکشنبه، ۶ = شنبه). مثال: «0 2 * * 6» یعنی هر شنبه ساعت ۲ بامداد.
          </span>
        </label>
      )}

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
        فعال باشد
      </label>

      <div className="rounded-lg bg-slate-50 p-3 text-sm">
        <p className="mb-1 font-medium text-slate-700">اجراهای بعدی</p>
        {preview.loading ? (
          <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> در حال محاسبه…</p>
        ) : preview.error ? (
          <p className="text-rose-700">{preview.error}</p>
        ) : (
          <ul className="space-y-0.5 text-slate-600">
            {preview.runs.map((run) => <li key={run}>{when(run)}</li>)}
          </ul>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          <X size={16} /> انصراف
        </button>
        <button type="submit" disabled={saving || Boolean(preview.error)} className="inline-flex items-center gap-1 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} ذخیره
        </button>
      </div>
    </form>
  );
}

export default function AdminSyncSchedulesSection() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | schedule
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(API);
      setSchedules(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      toastApiError(error, {}, "دریافت زمان‌بندی‌ها ناموفق بود.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axiosInstance.post(API, payload);
        toast.success("زمان‌بندی ذخیره شد.");
      } else {
        await axiosInstance.patch(`${API}${editing.id}/`, payload);
        toast.success("تغییرات زمان‌بندی ذخیره شد.");
      }
      setEditing(null);
      await load();
    } catch (error) {
      toastApiError(error, LABELS, "ذخیره زمان‌بندی ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (schedule) => {
    try {
      await axiosInstance.patch(`${API}${schedule.id}/`, { is_active: !schedule.is_active });
      await load();
    } catch (error) {
      toastApiError(error, LABELS, "تغییر وضعیت زمان‌بندی ناموفق بود.");
    }
  };

  const remove = async (schedule) => {
    if (!window.confirm(`زمان‌بندی «${schedule.name}» حذف شود؟`)) return;
    try {
      await axiosInstance.delete(`${API}${schedule.id}/`);
      toast.success("زمان‌بندی حذف شد.");
      await load();
    } catch (error) {
      toastApiError(error, {}, "حذف زمان‌بندی ناموفق بود.");
    }
  };

  return (
    <div dir="rtl" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <CalendarClock size={20} /> زمان‌بندی همگام‌سازی کامل سامانه جامع
          </h2>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            همگام‌سازی کامل در زمان‌های تعیین‌شده خودکار اجرا می‌شود. زمان‌ها به وقت تهران است و فاصله دو اجرا دست‌کم یک ساعت است.
            اگر همگام‌سازی قبلی هنوز در حال اجرا باشد، آن نوبت رد می‌شود.
          </p>
        </div>
        {editing === null && (
          <button type="button" onClick={() => setEditing("new")} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700">
            <Plus size={16} /> زمان‌بندی جدید
          </button>
        )}
      </div>

      {editing !== null && (
        <ScheduleForm
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? EMPTY_FORM : formFromSchedule(editing)}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> در حال دریافت…</p>
      ) : schedules.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          هنوز زمان‌بندی‌ای ندارید؛ همگام‌سازی فقط به‌صورت دستی از بخش «وظایف سامانه جامع» انجام می‌شود.
        </p>
      ) : (
        <ul className="space-y-3">
          {schedules.map((schedule) => (
            <li key={schedule.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1 text-sm">
                  <p className="font-semibold text-slate-900">
                    {schedule.name}
                    {!schedule.is_active && <span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">غیرفعال</span>}
                  </p>
                  <p className="text-slate-600">{describe(schedule)}</p>
                  <p className="text-slate-500">اجرای بعدی: {schedule.is_active ? when(schedule.next_run_at) : "—"}</p>
                  <p className="text-slate-500">
                    آخرین اجرا: {when(schedule.last_run_at)}
                    {schedule.last_outcome && (
                      <span className={`mr-2 rounded-full px-2 py-0.5 text-xs ${OUTCOME_TONES[schedule.last_outcome] || "bg-slate-100 text-slate-600"}`}>
                        {schedule.last_outcome_label}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    <input type="checkbox" checked={schedule.is_active} onChange={() => toggleActive(schedule)} />
                    فعال
                  </label>
                  <button type="button" onClick={() => setEditing(schedule)} aria-label={`ویرایش ${schedule.name}`} className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50">
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => remove(schedule)} aria-label={`حذف ${schedule.name}`} className="rounded-lg border p-2 text-rose-600 hover:bg-rose-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
